import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDownIcon, ChevronUpIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const SearchableSelect = ({ 
  options = [], 
  value = '', 
  onChange, 
  placeholder = 'Select an option', 
  allText = 'All',
  disabled = false,
  getOptionLabel = (option) => {
    if (typeof option === 'object' && option !== null) {
      return option.label || option.name || String(option.value || option.id || option);
    }
    return String(option || '');
  },
  getOptionValue = (option) => {
    if (typeof option === 'object' && option !== null) {
      return option.hasOwnProperty('value') ? option.value : (option.id !== undefined ? option.id : option);
    }
    return option;
  },
  className = '',
  maxHeight = '200px',
  showSearch = true,
  // Legacy props for backward compatibility
  items,
  onSelectItem,
  initialSelectedItem
}) => {
  // Handle legacy props - memoize to prevent infinite loops
  const actualOptions = useMemo(() => {
    return options.length > 0 ? options : items || [];
  }, [options, items]);
  
  const actualOnChange = onChange || ((value) => {
    if (onSelectItem) {
      const selectedOption = actualOptions.find(opt => getOptionValue(opt) === value);
      onSelectItem(selectedOption || null);
    }
  });
  const actualValue = value || (initialSelectedItem ? getOptionValue(initialSelectedItem) : '');

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(actualOptions);
  const [dropdownPosition, setDropdownPosition] = useState('below'); // 'below' or 'above'
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Determine dropdown position based on available space
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const buttonRect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const dropdownHeight = 250; // Approximate max height of dropdown

      // Open upward if not enough space below and more space above
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDropdownPosition('above');
      } else {
        setDropdownPosition('below');
      }
    }
  }, [isOpen]);

  // Update filtered options when search term or options change
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOptions(actualOptions);
    } else {
      const filtered = actualOptions.filter(option => {
        const label = getOptionLabel(option);
        return label.toLowerCase().includes(searchTerm.toLowerCase());
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, actualOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, showSearch]);

  const handleSelect = (option) => {
    const selectedValue = option === null ? '' : getOptionValue(option);
    actualOnChange(selectedValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const getDisplayValue = () => {
    if (!actualValue) return allText;
    
    const selectedOption = actualOptions.find(option => getOptionValue(option) === actualValue);
    const displayValue = selectedOption ? getOptionLabel(selectedOption) : actualValue;
    
    // Ensure we always return a string to prevent rendering objects
    return typeof displayValue === 'object' ? String(displayValue?.label || displayValue?.value || '') : String(displayValue || '');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Select Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-2 py-1.5 text-sm text-left border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white 
          ${disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : 'hover:border-gray-400 cursor-pointer'}
          ${isOpen ? 'ring-1 ring-blue-500 border-blue-500' : ''}
        `}
      >
        <div className="flex items-center justify-between">
          <span className={`block truncate ${!actualValue ? 'text-gray-500' : ''}`}>
            {getDisplayValue()}
          </span>
          {!disabled && (
            isOpen ? (
              <ChevronUpIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            )
          )}
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div 
          className={`absolute z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg ${
            dropdownPosition === 'above' ? 'bottom-full mb-1' : 'mt-1'
          }`}
        >
          {/* Search Input */}
          {showSearch && (
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search..."
                  className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto" style={{ maxHeight }}>
            {/* "All" option */}
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none
                ${!actualValue ? 'bg-blue-100 text-blue-900 font-medium' : 'text-gray-900'}
              `}
            >
              {allText}
            </button>

            {/* Filtered Options */}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const optionValue = getOptionValue(option);
                const optionLabel = getOptionLabel(option);
                const isSelected = actualValue === optionValue;

                return (
                  <button
                    key={optionValue || index}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none
                      ${isSelected ? 'bg-blue-100 text-blue-900 font-medium' : 'text-gray-900'}
                    `}
                  >
                    {optionLabel}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500 italic">
                No options found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
